import { CircularProgress, Divider, Link, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { produce } from "immer";
import React, { Fragment, memo, useContext, useEffect, useMemo, useState } from "react";
import { ReactComponent as LinkedInIcon } from '../../../assets/icons/linkedin.svg';
import { AnsweredQuestion } from '../../../contexts/DashboardContext';
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { getDashboardQueriesFunc } from "../../../lib/helper";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, DashboardQuery } from "../../../types/files";
import RecentPostsAccordion from '../../atoms/RecentPostsAccordion';

const useStyles = makeStyles((theme) => ({
    card: {
        width: 450,
        maxWidth: 450,
        minHeight: 200,
        maxHeight: 400,
        height: 'auto',
        padding: 16,
        paddingRight: 24,
        overflow: 'hidden',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['600'],
        maxWidth: '24ch',
        textOverflow: 'ellipsis',
    },
    icon: {
        width: 28,
        height: 28,
        fill: '#7bd4d4',
    },
}));

const LinkedInPostsContentCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);
    const [linkedInLoaded, setLinkedInLoaded] = useState<boolean>(false);

    const linkedInPosts = useMemo(() =>
        mappedOverviewQueries.get(`${dashboard.id}:LinkedIn Posts`)
    , [dashboard.id, mappedOverviewQueries]);

    const website = useMemo(() =>
        mappedOverviewQueries.get(`${dashboard.id}:LinkedIn`)
    , [dashboard.id, mappedOverviewQueries]);

    useEffect(() => {
        if (!linkedInLoaded) {
            Promise.resolve(
                getDashboardQueriesFunc(dashboard!.id, 'LinkedIn')
            ).then((bulkQueryData: DashboardQuery[]) => {
                setDashboardsQueries(prev => produce(prev, draft => {
                    bulkQueryData.forEach(newQuery => {
                        if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
                            draft.push(newQuery);
                    });
                }));
                setLinkedInLoaded(true);
            });
        }
    // eslint-disable-next-line
    }, [dashboard]);

    if (!Array.isArray(linkedInPosts) || linkedInPosts.length === 0)
        return (<></>);

    return (<>
        <Stack className={classes.card} alignItems="flex-start" justifyContent="stretch">
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" mb={1}>
                <Typography className={classes.title}>
                    {`Recent Posts for ${dashboard.title}`}
                </Typography>
                {!linkedInLoaded ? (<CircularProgress />) : (
                    <Link href={website}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{ cursor: 'pointer' }}>
                        <LinkedInIcon className={classes.icon} />
                    </Link>
                )}
            </Stack>
            <Divider sx={{ width: '100%' }} />
            <Stack spacing={1} alignItems="flex-start" justifyContent="stretch" width="100%" height="100%"
                onClick={(e) => {e.stopPropagation(); e.preventDefault();}}
                sx={{ overflowY: 'auto', overflowX: 'hidden', ...scrollbarStyle }}>
                {linkedInPosts?.map((element: AnsweredQuestion, i) => (
                    <Fragment key={'linkedin-posts-content-card-58-' + i}>
                        <Divider sx={{ width: '100%' }} />
                        <RecentPostsAccordion element={element} />
                    </Fragment>
                ))}
            </Stack>
        </Stack>
    </>);
};

export default memo(LinkedInPostsContentCard);
